import { useState } from "react"
import { ALL_PROJECTS } from "@aix/aixinternal-shared"
import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from "@aix/ui"

import { trpc } from "~/lib/trpc"

/**
 * On-call admin screen.
 *
 * Five tabs:
 *   Now       — who is on-call right now per project (live)
 *   Layers    — recurring rotations (list + add)
 *   Overrides — one-off swaps (list + add)
 *   Policies  — escalation policies (list, read-only — edit via CLI)
 *   Users     — paging profile (phone/slack/tz)
 */
export default function OnCallScreen() {
  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      <h1 className="mb-1 text-2xl font-bold text-gray-900">On-call</h1>
      <p className="mb-6 text-sm text-gray-500">
        Schedules, overrides, escalation. Resolution: override → highest-priority layer → defaults → env fallback.
      </p>

      <Tabs defaultValue="now">
        <TabsList>
          <TabsTrigger value="now">Now</TabsTrigger>
          <TabsTrigger value="layers">Layers</TabsTrigger>
          <TabsTrigger value="overrides">Overrides</TabsTrigger>
          <TabsTrigger value="policies">Policies</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
        </TabsList>
        <TabsContent value="now">
          <NowTab />
        </TabsContent>
        <TabsContent value="layers">
          <LayersTab />
        </TabsContent>
        <TabsContent value="overrides">
          <OverridesTab />
        </TabsContent>
        <TabsContent value="policies">
          <PoliciesTab />
        </TabsContent>
        <TabsContent value="users">
          <UsersTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ============================================================================
// NOW — who's on-call per project, plus the global slot
// ============================================================================

function NowTab() {
  const slots = ["__global__", ...ALL_PROJECTS] as const
  return (
    <div className="mt-4 overflow-hidden rounded-lg border bg-white">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
          <tr>
            <th className="px-4 py-2">Project</th>
            <th className="px-4 py-2">Person</th>
            <th className="px-4 py-2">Phone</th>
            <th className="px-4 py-2">Slack</th>
            <th className="px-4 py-2">Source</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {slots.map((slot) => (
            <NowRow key={slot} project={slot === "__global__" ? null : slot} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function NowRow({ project }: { project: string | null }) {
  const { data, isLoading } = trpc.oncall.current.useQuery({ project }, { refetchInterval: 30_000 })
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-2 font-mono text-xs text-gray-700">{project ?? "(global)"}</td>
      <td className="px-4 py-2">
        {isLoading ? (
          <span className="text-gray-400">…</span>
        ) : data?.userId ? (
          <span>
            <span className="font-medium text-gray-900">{data.name}</span>
            <span className="ml-2 text-xs text-gray-500">{data.email}</span>
          </span>
        ) : (
          <span className="text-amber-600">{data?.name ?? "(none)"}</span>
        )}
      </td>
      <td className="px-4 py-2 font-mono text-xs">
        <span className={data?.phone ? "text-gray-700" : "text-amber-600"}>{data?.phone ?? "—"}</span>
      </td>
      <td className="px-4 py-2 font-mono text-xs text-gray-700">{data?.slackUserId ?? "—"}</td>
      <td className="px-4 py-2 font-mono text-xs text-gray-500">{data?.source ?? "—"}</td>
    </tr>
  )
}

// ============================================================================
// LAYERS
// ============================================================================

function LayersTab() {
  const { data, isLoading, refetch } = trpc.oncall.layerList.useQuery({})
  const remove = trpc.oncall.layerRemove.useMutation({ onSuccess: () => void refetch() })
  const toggle = trpc.oncall.layerSetActive.useMutation({ onSuccess: () => void refetch() })

  return (
    <div className="mt-4 space-y-4">
      <AddLayerForm onAdded={() => void refetch()} />
      <div className="overflow-hidden rounded-lg border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Active</th>
              <th className="px-3 py-2">Pri</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Project</th>
              <th className="px-3 py-2">Window (local)</th>
              <th className="px-3 py-2">Days</th>
              <th className="px-3 py-2">Length</th>
              <th className="px-3 py-2">Users</th>
              <th className="px-3 py-2">TZ</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading && (
              <tr>
                <td colSpan={10} className="px-3 py-4 text-center text-gray-400">
                  Loading…
                </td>
              </tr>
            )}
            {data?.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-4 text-center text-gray-400">
                  No layers. Add one above.
                </td>
              </tr>
            )}
            {data?.map((l) => (
              <tr key={l.id} className="hover:bg-gray-50">
                <td className="px-3 py-2">
                  <button
                    onClick={() => toggle.mutate({ id: l.id, active: !l.active })}
                    className={`text-xs ${l.active ? "text-green-600" : "text-gray-400"}`}
                  >
                    {l.active ? "✅" : "⛔"}
                  </button>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{l.priority}</td>
                <td className="px-3 py-2 font-medium">{l.name}</td>
                <td className="px-3 py-2 font-mono text-xs">{l.project ?? "(global)"}</td>
                <td className="px-3 py-2 font-mono text-xs">
                  {l.dailyStartLocal && l.dailyEndLocal ? `${l.dailyStartLocal}–${l.dailyEndLocal}` : "all day"}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{l.weekdays?.join(",") ?? "all"}</td>
                <td className="px-3 py-2 font-mono text-xs">{(l.rotationLengthSeconds / 86400).toFixed(1)}d</td>
                <td className="px-3 py-2 font-mono text-xs">{l.rotationUsers.length}</td>
                <td className="px-3 py-2 font-mono text-[10px] text-gray-500">{l.timezone}</td>
                <td className="px-3 py-2">
                  <button
                    onClick={() => {
                      if (confirm(`Remove layer "${l.name}"?`)) remove.mutate({ id: l.id })
                    }}
                    className="text-xs text-red-600 hover:underline"
                  >
                    delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function AddLayerForm({ onAdded }: { onAdded: () => void }) {
  const usersQ = trpc.oncall.listUsers.useQuery()
  const add = trpc.oncall.layerAdd.useMutation({
    onSuccess: () => {
      onAdded()
      setEmails("")
      setName("")
    },
  })
  const [name, setName] = useState("")
  const [project, setProject] = useState("")
  const [emails, setEmails] = useState("")
  const [start, setStart] = useState(new Date().toISOString().slice(0, 16))
  const [lengthDays, setLengthDays] = useState(1)
  const [dailyStart, setDailyStart] = useState("")
  const [dailyEnd, setDailyEnd] = useState("")
  const [tz, setTz] = useState("America/Los_Angeles")
  const [priority, setPriority] = useState(0)

  return (
    <div className="rounded-lg border bg-white p-4">
      <h3 className="mb-3 text-sm font-semibold">Add layer</h3>
      <div className="grid grid-cols-3 gap-3 text-sm">
        <input
          className={inputCn}
          placeholder="Name (e.g. Nightly cover)"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <select className={inputCn} value={project} onChange={(e) => setProject(e.target.value)}>
          <option value="">(global — all projects)</option>
          {ALL_PROJECTS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <input
          className={inputCn}
          placeholder="Priority (higher wins)"
          type="number"
          value={priority}
          onChange={(e) => setPriority(parseInt(e.target.value, 10) || 0)}
        />
        <select
          className={inputCn + " col-span-3"}
          multiple
          value={emails.split(",").filter(Boolean)}
          onChange={(e) => {
            const sel = Array.from(e.target.selectedOptions).map((o) => o.value)
            setEmails(sel.join(","))
          }}
        >
          {usersQ.data
            ?.filter((u) => u.isActive)
            .map((u) => (
              <option key={u.email} value={u.email}>
                {u.name} &lt;{u.email}&gt; {u.phone ? "📞" : "(no phone)"}
              </option>
            ))}
        </select>
        <input className={inputCn} type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
        <input
          className={inputCn}
          placeholder="Length (days)"
          type="number"
          step="0.5"
          value={lengthDays}
          onChange={(e) => setLengthDays(parseFloat(e.target.value) || 1)}
        />
        <input
          className={inputCn}
          placeholder="Timezone (e.g. America/Los_Angeles)"
          value={tz}
          onChange={(e) => setTz(e.target.value)}
        />
        <input
          className={inputCn}
          placeholder="Daily start (HH:MM, optional)"
          value={dailyStart}
          onChange={(e) => setDailyStart(e.target.value)}
        />
        <input
          className={inputCn}
          placeholder="Daily end (HH:MM, optional)"
          value={dailyEnd}
          onChange={(e) => setDailyEnd(e.target.value)}
        />
        <Button
          onClick={() => {
            const userEmails = emails
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
            if (userEmails.length === 0 || !name) return
            add.mutate({
              name,
              project: project || null,
              priority,
              rotationUserEmails: userEmails,
              rotationStart: new Date(start).toISOString(),
              rotationLengthSeconds: Math.round(lengthDays * 86400),
              dailyStartLocal: dailyStart || undefined,
              dailyEndLocal: dailyEnd || undefined,
              timezone: tz,
            })
          }}
        >
          Add layer
        </Button>
      </div>
      {add.error && <div className="mt-2 text-xs text-red-600">{add.error.message}</div>}
    </div>
  )
}

// ============================================================================
// OVERRIDES
// ============================================================================

function OverridesTab() {
  const { data, isLoading, refetch } = trpc.oncall.overrideList.useQuery({ upcomingOnly: true })
  const remove = trpc.oncall.overrideRemove.useMutation({ onSuccess: () => void refetch() })
  const usersQ = trpc.oncall.listUsers.useQuery()
  const add = trpc.oncall.overrideAdd.useMutation({ onSuccess: () => void refetch() })

  const [user, setUser] = useState("")
  const [project, setProject] = useState("")
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 16))
  const [to, setTo] = useState(new Date(Date.now() + 86_400_000).toISOString().slice(0, 16))
  const [reason, setReason] = useState("")

  return (
    <div className="mt-4 space-y-4">
      <div className="rounded-lg border bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold">Add override (one-off swap)</h3>
        <div className="grid grid-cols-3 gap-3 text-sm">
          <select className={inputCn} value={user} onChange={(e) => setUser(e.target.value)}>
            <option value="">— select user —</option>
            {usersQ.data
              ?.filter((u) => u.isActive)
              .map((u) => (
                <option key={u.email} value={u.email}>
                  {u.name} &lt;{u.email}&gt;
                </option>
              ))}
          </select>
          <select className={inputCn} value={project} onChange={(e) => setProject(e.target.value)}>
            <option value="">(global)</option>
            {ALL_PROJECTS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <input
            className={inputCn}
            placeholder="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <input className={inputCn} type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
          <input className={inputCn} type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
          <Button
            disabled={!user}
            onClick={() => {
              add.mutate({
                userEmail: user,
                project: project || null,
                startsAt: new Date(from).toISOString(),
                endsAt: new Date(to).toISOString(),
                reason: reason || undefined,
              })
            }}
          >
            Add override
          </Button>
        </div>
        {add.error && <div className="mt-2 text-xs text-red-600">{add.error.message}</div>}
      </div>

      <div className="overflow-hidden rounded-lg border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Project</th>
              <th className="px-3 py-2">User</th>
              <th className="px-3 py-2">From</th>
              <th className="px-3 py-2">To</th>
              <th className="px-3 py-2">Reason</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-gray-400">
                  Loading…
                </td>
              </tr>
            )}
            {data?.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-gray-400">
                  No upcoming overrides.
                </td>
              </tr>
            )}
            {data?.map((o) => (
              <tr key={o.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-mono text-xs">{o.project ?? "(global)"}</td>
                <td className="px-3 py-2">{o.userName ?? o.userEmail}</td>
                <td className="px-3 py-2 font-mono text-xs">{new Date(o.startsAt).toLocaleString()}</td>
                <td className="px-3 py-2 font-mono text-xs">{new Date(o.endsAt).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{o.reason}</td>
                <td className="px-3 py-2">
                  <button
                    onClick={() => {
                      if (confirm("Remove override?")) remove.mutate({ id: o.id })
                    }}
                    className="text-xs text-red-600 hover:underline"
                  >
                    delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ============================================================================
// POLICIES (read-only for now; edit via CLI)
// ============================================================================

function PoliciesTab() {
  const { data, isLoading } = trpc.oncall.policyList.useQuery({})
  return (
    <div className="mt-4 space-y-3">
      <p className="text-xs text-gray-500">
        Edit policies via CLI:{" "}
        <code className="rounded bg-gray-100 px-1">/api-aix-uptime-monitor-incidents oncall policy:set …</code>.
        Built-in default (when no row exists): notify@0s, notify@5min, escalate@10min.
      </p>
      {isLoading && <div className="text-sm text-gray-400">Loading…</div>}
      {data?.length === 0 && (
        <div className="rounded-lg border bg-amber-50 p-4 text-sm text-amber-700">
          No policies set — using built-in default.
        </div>
      )}
      {data?.map((p) => (
        <div key={p.id} className="rounded-lg border bg-white p-4">
          <div className="mb-2 flex items-center justify-between">
            <div>
              <span className="font-semibold">{p.name}</span>
              {p.isDefault && (
                <span className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">default</span>
              )}
              <span className="ml-2 font-mono text-xs text-gray-500">{p.project ?? "(global)"}</span>
            </div>
          </div>
          <ol className="space-y-1">
            {p.steps.map((s, i) => (
              <li key={i} className="flex items-center gap-3 text-sm">
                <span className="w-16 font-mono text-xs text-gray-500">+{s.delaySeconds}s</span>
                <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] uppercase">{s.kind}</span>
                <span className="text-gray-700">{s.label}</span>
              </li>
            ))}
          </ol>
        </div>
      ))}
    </div>
  )
}

// ============================================================================
// USERS
// ============================================================================

function UsersTab() {
  const { data, isLoading, refetch } = trpc.oncall.listUsers.useQuery()
  const setProfile = trpc.oncall.setProfile.useMutation({ onSuccess: () => void refetch() })

  return (
    <div className="mt-4 overflow-hidden rounded-lg border bg-white">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
          <tr>
            <th className="px-3 py-2">User</th>
            <th className="px-3 py-2">Phone</th>
            <th className="px-3 py-2">Slack ID</th>
            <th className="px-3 py-2">Timezone</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {isLoading && (
            <tr>
              <td colSpan={5} className="px-3 py-4 text-center text-gray-400">
                Loading…
              </td>
            </tr>
          )}
          {data
            ?.filter((u) => u.isActive)
            .map((u) => (
              <UserRow key={u.email} user={u} onSave={(patch) => setProfile.mutate({ userEmail: u.email, ...patch })} />
            ))}
        </tbody>
      </table>
    </div>
  )
}

function UserRow({
  user,
  onSave,
}: {
  user: { email: string; name: string; phone: string | null; slackUserId: string | null; timezone: string | null }
  onSave: (patch: { phone?: string; slackUserId?: string; timezone?: string }) => void
}) {
  const [phone, setPhone] = useState(user.phone ?? "")
  const [slack, setSlack] = useState(user.slackUserId ?? "")
  const [tz, setTz] = useState(user.timezone ?? "")
  const dirty = phone !== (user.phone ?? "") || slack !== (user.slackUserId ?? "") || tz !== (user.timezone ?? "")
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-3 py-2">
        <div className="font-medium">{user.name}</div>
        <div className="text-xs text-gray-500">{user.email}</div>
      </td>
      <td className="px-3 py-2">
        <input
          className={inputCn}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+15551234567"
        />
      </td>
      <td className="px-3 py-2">
        <input className={inputCn} value={slack} onChange={(e) => setSlack(e.target.value)} placeholder="U05..." />
      </td>
      <td className="px-3 py-2">
        <input
          className={inputCn}
          value={tz}
          onChange={(e) => setTz(e.target.value)}
          placeholder="America/Los_Angeles"
        />
      </td>
      <td className="px-3 py-2">
        <Button
          size="sm"
          variant={dirty ? "default" : "outline"}
          disabled={!dirty}
          onClick={() => onSave({ phone, slackUserId: slack, timezone: tz })}
        >
          Save
        </Button>
      </td>
    </tr>
  )
}

const inputCn =
  "w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:border-blue-500 focus:outline-hidden"
